import React, { useState, useEffect, useMemo } from 'react';
import soundService from '../utils/soundService';

// Dictionary data organized by categories
const categories = {
  greetings: {
    name: "Greetings",
    nameSi: "සුභ පැතුම්",
    icon: "👋",
    color: "#4CAF50",
    words: [
      { english: "hello", sinhala: "ආයුබෝවන්", pronunciation: "aa-yoo-boh-vuhn", example: "ආයුබෝවන්, කොහොමද?", exampleEn: "Hello, how are you?", spoken: { sinhala: "හායි", pronunciation: "haa-yi" } },
      { english: "hi", sinhala: "හායි", pronunciation: "haa-yee" },
      { english: "goodbye", sinhala: "ගිහින් එන්නම්", pronunciation: "gee-hin ehn-nuhm", example: "ගිහින් එන්නම්, පසුව හමුවෙමු", exampleEn: "Goodbye, see you later", spoken: { sinhala: "බයි", pronunciation: "bai" } },
      { english: "bye", sinhala: "බයි", pronunciation: "bai" },
      { english: "thank you", sinhala: "ස්තූතියි", pronunciation: "sthoo-thee-yee", example: "උදව්වට ස්තූතියි", exampleEn: "Thank you for the help", spoken: { sinhala: "තෑන්ක්ස්", pronunciation: "tanks" } },
      { english: "please", sinhala: "කරුණාකර", pronunciation: "kuh-roo-naa-kuh-ruh", spoken: { sinhala: "ප්ලීස්", pronunciation: "pleez" } },
      { english: "sorry", sinhala: "සමාවන්න", pronunciation: "suh-maa-vuhn-nuh", spoken: { sinhala: "සොරි", pronunciation: "soh-ri" } },
      { english: "yes", sinhala: "ඔව්", pronunciation: "ohv" },
      { english: "no", sinhala: "නැහැ", pronunciation: "neh-heh" },
      { english: "okay", sinhala: "හරි", pronunciation: "huh-ree", spoken: { sinhala: "ඕකේ", pronunciation: "oh-kay" } },
      { english: "welcome", sinhala: "ආයුබෝවන්", pronunciation: "aa-yoo-boh-vuhn" },
      { english: "good morning", sinhala: "සුභ උදෑසනක්", pronunciation: "soo-buh oo-deh-suh-nuhk" },
      { english: "good night", sinhala: "සුභ රාත්‍රියක්", pronunciation: "soo-buh raa-three-yuhk" },
      { english: "how are you", sinhala: "කොහොමද?", pronunciation: "koh-hoh-muh-duh" },
      { english: "i am fine", sinhala: "මම හොඳින්", pronunciation: "muh-muh hohn-din" },
    ]
  },
  family: {
    name: "Family",
    nameSi: "පවුල",
    icon: "👨‍👩‍👧‍👦",
    color: "#E91E63",
    words: [
      { english: "mother", sinhala: "අම්මා", pronunciation: "uhm-maa", example: "මගේ අම්මා ලස්සනයි", exampleEn: "My mother is beautiful" },
      { english: "father", sinhala: "තාත්තා", pronunciation: "thaa-thaa" },
      { english: "sister", sinhala: "අක්කා/නංගි", pronunciation: "uhk-kaa/nuhn-gee" },
      { english: "brother", sinhala: "අයියා/මල්ලි", pronunciation: "uh-yee-yaa/muhl-lee" },
      { english: "grandmother", sinhala: "ආච්චි", pronunciation: "aach-chee" },
      { english: "grandfather", sinhala: "සීයා", pronunciation: "see-yaa" },
      { english: "uncle", sinhala: "මාමා", pronunciation: "maa-maa" },
      { english: "aunt", sinhala: "නැන්දා", pronunciation: "nehn-daa" },
      { english: "son", sinhala: "පුතා", pronunciation: "poo-thaa" },
      { english: "daughter", sinhala: "දුව", pronunciation: "doo-vuh" },
      { english: "husband", sinhala: "සැමියා", pronunciation: "seh-mee-yaa", spoken: { sinhala: "මිනිහා", pronunciation: "mee-nee-haa" } },
      { english: "wife", sinhala: "බිරිඳ", pronunciation: "bee-rin-duh", spoken: { sinhala: "ගෑනි", pronunciation: "gae-nee" } },
      { english: "baby", sinhala: "බබා", pronunciation: "buh-baa" },
      { english: "child", sinhala: "ළමයා", pronunciation: "luh-muh-yaa", spoken: { sinhala: "ලමයා", pronunciation: "luh-muh-yaa" } },
    ]
  },
  numbers: {
    name: "Numbers",
    nameSi: "අංක",
    icon: "🔢",
    color: "#2196F3",
    words: [
      { english: "one", sinhala: "එක", pronunciation: "eh-kuh" },
      { english: "two", sinhala: "දෙක", pronunciation: "deh-kuh" },
      { english: "three", sinhala: "තුන", pronunciation: "thoo-nuh" },
      { english: "four", sinhala: "හතර", pronunciation: "huh-thuh-ruh" },
      { english: "five", sinhala: "පහ", pronunciation: "puh-huh" },
      { english: "six", sinhala: "හය", pronunciation: "huh-yuh" },
      { english: "seven", sinhala: "හත", pronunciation: "huh-thuh" },
      { english: "eight", sinhala: "අට", pronunciation: "uh-tuh" },
      { english: "nine", sinhala: "නවය", pronunciation: "nuh-vuh-yuh" },
      { english: "ten", sinhala: "දහය", pronunciation: "duh-huh-yuh" },
      { english: "twenty", sinhala: "විස්ස", pronunciation: "vis-suh" },
      { english: "fifty", sinhala: "පනහ", pronunciation: "puh-nuh-huh" },
      { english: "hundred", sinhala: "සියය", pronunciation: "see-yuh-yuh" },
      { english: "thousand", sinhala: "දහස", pronunciation: "duh-huh-suh" },
    ]
  },
  colors: {
    name: "Colors",
    nameSi: "වර්ණ",
    icon: "🎨",
    color: "#9C27B0",
    words: [
      { english: "red", sinhala: "රතු", pronunciation: "ruh-thoo" },
      { english: "blue", sinhala: "නිල්", pronunciation: "nil" },
      { english: "green", sinhala: "කොළ", pronunciation: "koh-luh" },
      { english: "yellow", sinhala: "කහ", pronunciation: "kuh-huh" },
      { english: "white", sinhala: "සුදු", pronunciation: "soo-doo" },
      { english: "black", sinhala: "කළු", pronunciation: "kuh-loo" },
      { english: "orange", sinhala: "තැඹිලි", pronunciation: "thehm-bee-lee" },
      { english: "pink", sinhala: "රෝස", pronunciation: "roh-suh" },
      { english: "purple", sinhala: "දම්", pronunciation: "duhm" },
      { english: "brown", sinhala: "දුඹුරු", pronunciation: "doom-boo-roo" },
      { english: "gray", sinhala: "අළු", pronunciation: "uh-loo" },
      { english: "gold", sinhala: "රන්", pronunciation: "ruhn" },
    ]
  },
  animals: {
    name: "Animals",
    nameSi: "සතුන්",
    icon: "🐾",
    color: "#FF9800",
    words: [
      { english: "dog", sinhala: "බල්ලා", pronunciation: "buhl-laa" },
      { english: "cat", sinhala: "පූසා", pronunciation: "poo-saa" },
      { english: "bird", sinhala: "කුරුල්ලා", pronunciation: "koo-rool-laa" },
      { english: "fish", sinhala: "මාළුවා", pronunciation: "maa-loo-vaa" },
      { english: "elephant", sinhala: "අලියා", pronunciation: "uh-lee-yaa" },
      { english: "monkey", sinhala: "වඳුරා", pronunciation: "vuhn-doo-raa" },
      { english: "cow", sinhala: "හරකා", pronunciation: "huh-ruh-kaa" },
      { english: "horse", sinhala: "අශ්වයා", pronunciation: "uhsh-vuh-yaa" },
      { english: "lion", sinhala: "සිංහයා", pronunciation: "sin-huh-yaa" },
      { english: "tiger", sinhala: "කොටියා", pronunciation: "koh-tee-yaa" },
      { english: "bear", sinhala: "වලහා", pronunciation: "vuh-luh-haa" },
      { english: "deer", sinhala: "මුවා", pronunciation: "moo-vaa" },
      { english: "rabbit", sinhala: "හාවා", pronunciation: "haa-vaa" },
      { english: "snake", sinhala: "සර්පයා", pronunciation: "suhr-puh-yaa", spoken: { sinhala: "නයා", pronunciation: "nuh-yaa" } },
      { english: "butterfly", sinhala: "සමනලයා", pronunciation: "suh-muh-nuh-luh-yaa" },
      { english: "peacock", sinhala: "මොණරා", pronunciation: "moh-nuh-raa" },
      { english: "parrot", sinhala: "ගිරවා", pronunciation: "gee-ruh-vaa" },
      { english: "crow", sinhala: "කපුටා", pronunciation: "kuh-poo-taa", spoken: { sinhala: "කාකා", pronunciation: "kaa-kaa" } },
    ]
  },
  food: {
    name: "Food & Drinks",
    nameSi: "ආහාර සහ පාන",
    icon: "🍽️",
    color: "#F44336",
    words: [
      { english: "rice", sinhala: "බත්", pronunciation: "buhth", example: "මම බත් කනවා", exampleEn: "I eat rice" },
      { english: "water", sinhala: "වතුර", pronunciation: "vuh-thoo-ruh" },
      { english: "tea", sinhala: "තේ", pronunciation: "thay" },
      { english: "coffee", sinhala: "කෝපි", pronunciation: "koh-pee" },
      { english: "milk", sinhala: "කිරි", pronunciation: "kee-ree" },
      { english: "bread", sinhala: "පාන්", pronunciation: "paan" },
      { english: "egg", sinhala: "බිත්තර", pronunciation: "bith-thuh-ruh" },
      { english: "meat", sinhala: "මස්", pronunciation: "muhs" },
      { english: "fruit", sinhala: "පලතුරු", pronunciation: "puh-luh-thoo-roo" },
      { english: "vegetable", sinhala: "එළවළු", pronunciation: "eh-luh-vuh-loo" },
      { english: "banana", sinhala: "කෙසෙල්", pronunciation: "keh-sehl" },
      { english: "mango", sinhala: "අඹ", pronunciation: "uhm-buh" },
      { english: "coconut", sinhala: "පොල්", pronunciation: "pohl" },
      { english: "sugar", sinhala: "සීනි", pronunciation: "see-nee" },
      { english: "salt", sinhala: "ලුණු", pronunciation: "loo-noo" },
      { english: "cake", sinhala: "කේක්", pronunciation: "kayk" },
      { english: "soup", sinhala: "සුප්", pronunciation: "soop" },
      { english: "juice", sinhala: "යුෂ", pronunciation: "yoo-shuh", spoken: { sinhala: "ජූස්", pronunciation: "joos" } },
    ]
  },
  sriLankanDishes: {
    name: "Sri Lankan Dishes",
    nameSi: "ශ්‍රී ලාංකික ආහාර",
    icon: "🍛",
    color: "#D84315",
    words: [
      { english: "rice and curry", sinhala: "බත් සහ කරි", pronunciation: "buhth suh-huh kuh-ree", example: "ශ්‍රී ලංකාවේ ප්‍රධාන ආහාරය බත් සහ කරි", exampleEn: "Rice and curry is the main food of Sri Lanka" },
      { english: "kottu", sinhala: "කොත්තු", pronunciation: "kohth-thoo", example: "කොත්තු රොටි ඉතා රසයි", exampleEn: "Kottu roti is very tasty" },
      { english: "hoppers", sinhala: "ආප්ප", pronunciation: "aap-puh" },
      { english: "egg hoppers", sinhala: "බිත්තර ආප්ප", pronunciation: "bith-thuh-ruh aap-puh" },
      { english: "string hoppers", sinhala: "ඉඳිආප්ප", pronunciation: "in-dee-aap-puh" },
      { english: "milk rice", sinhala: "කිරිබත්", pronunciation: "kee-ree-buhth" },
      { english: "lamprais", sinhala: "ලම්ප්‍රයිස්", pronunciation: "luhm-prais" },
      { english: "pittu", sinhala: "පිට්ටු", pronunciation: "pit-too" },
      { english: "pol sambol", sinhala: "පොල් සම්බෝල්", pronunciation: "pohl suhm-bohl" },
      { english: "dhal curry", sinhala: "පරිප්පු", pronunciation: "puh-rip-poo" },
      { english: "fish curry", sinhala: "මාළු කරි", pronunciation: "maa-loo kuh-ree" },
      { english: "chicken curry", sinhala: "කුකුල් මස් කරි", pronunciation: "koo-kool muhs kuh-ree" },
      { english: "watalappan", sinhala: "වටලප්පන්", pronunciation: "vuh-tuh-luhp-puhn" },
      { english: "kavum", sinhala: "කැවුම්", pronunciation: "keh-voom" },
      { english: "kokis", sinhala: "කොකිස්", pronunciation: "koh-kis" },
      { english: "achcharu", sinhala: "අච්චාරු", pronunciation: "uhch-chaa-roo" },
      { english: "pol roti", sinhala: "පොල් රොටි", pronunciation: "pohl roh-tee" },
      { english: "godamba roti", sinhala: "ගොඩම්බ රොටි", pronunciation: "goh-duhm-buh roh-tee" },
      { english: "isso wade", sinhala: "ඉස්සෝ වඩේ", pronunciation: "is-soh vuh-day" },
      { english: "cutlets", sinhala: "කට්ලට්", pronunciation: "kuht-luht" },
    ]
  },
  bodyParts: {
    name: "Body Parts",
    nameSi: "ශරීර කොටස්",
    icon: "🫀",
    color: "#795548",
    words: [
      { english: "head", sinhala: "ඔළුව", pronunciation: "oh-loo-vuh" },
      { english: "eye", sinhala: "ඇස", pronunciation: "eh-suh" },
      { english: "ear", sinhala: "කන", pronunciation: "kuh-nuh" },
      { english: "nose", sinhala: "නාසය", pronunciation: "naa-suh-yuh" },
      { english: "mouth", sinhala: "කට", pronunciation: "kuh-tuh" },
      { english: "hand", sinhala: "අත", pronunciation: "uh-thuh" },
      { english: "foot", sinhala: "පාදය", pronunciation: "paa-duh-yuh" },
      { english: "face", sinhala: "මුහුණ", pronunciation: "moo-hoo-nuh" },
      { english: "hair", sinhala: "කෙස්", pronunciation: "kehs" },
      { english: "neck", sinhala: "බෙල්ල", pronunciation: "behl-luh" },
      { english: "shoulder", sinhala: "උරහිස", pronunciation: "oo-ruh-hee-suh" },
      { english: "finger", sinhala: "ඇඟිල්ල", pronunciation: "ehn-geel-luh" },
      { english: "stomach", sinhala: "බඩ", pronunciation: "buh-duh" },
      { english: "heart", sinhala: "හදවත", pronunciation: "huh-duh-vuh-thuh" },
      { english: "brain", sinhala: "මොළය", pronunciation: "moh-luh-yuh" },
    ]
  },
  nature: {
    name: "Nature",
    nameSi: "ස්වභාවය",
    icon: "🌿",
    color: "#4CAF50",
    words: [
      { english: "sun", sinhala: "හිරු", pronunciation: "hee-roo" },
      { english: "moon", sinhala: "හඳ", pronunciation: "huhn-duh" },
      { english: "star", sinhala: "තරුව", pronunciation: "thuh-roo-vuh" },
      { english: "sky", sinhala: "අහස", pronunciation: "uh-huh-suh" },
      { english: "rain", sinhala: "වැස්ස", pronunciation: "vehs-suh" },
      { english: "cloud", sinhala: "වලාකුළ", pronunciation: "vuh-laa-koo-luh" },
      { english: "tree", sinhala: "ගස", pronunciation: "guh-suh" },
      { english: "flower", sinhala: "මල", pronunciation: "muh-luh" },
      { english: "river", sinhala: "ගඟ", pronunciation: "guhn-guh" },
      { english: "sea", sinhala: "මුහුද", pronunciation: "moo-hoo-duh" },
      { english: "mountain", sinhala: "කන්ද", pronunciation: "kuhn-duh" },
      { english: "earth", sinhala: "පොළොව", pronunciation: "poh-loh-vuh" },
      { english: "forest", sinhala: "වනය", pronunciation: "vuh-nuh-yuh" },
      { english: "beach", sinhala: "වෙරළ", pronunciation: "veh-ruh-luh" },
      { english: "island", sinhala: "දූපත", pronunciation: "doo-puh-thuh" },
    ]
  },
  places: {
    name: "Places",
    nameSi: "ස්ථාන",
    icon: "🏛️",
    color: "#607D8B",
    words: [
      { english: "home", sinhala: "ගෙදර", pronunciation: "geh-duh-ruh" },
      { english: "school", sinhala: "පාසල", pronunciation: "paa-suh-luh", spoken: { sinhala: "ස්කූල්", pronunciation: "skool" } },
      { english: "hospital", sinhala: "රෝහල", pronunciation: "roh-huh-luh", spoken: { sinhala: "හොස්පිටල්", pronunciation: "hos-pee-tuhl" } },
      { english: "market", sinhala: "වෙළඳ පොළ", pronunciation: "veh-luhn-duh poh-luh" },
      { english: "temple", sinhala: "පන්සල", pronunciation: "puhn-suh-luh" },
      { english: "bank", sinhala: "බැංකුව", pronunciation: "behn-koo-vuh" },
      { english: "hotel", sinhala: "හෝටලය", pronunciation: "hoh-tuh-luh-yuh", spoken: { sinhala: "හෝටල්", pronunciation: "hoh-tuhl" } },
      { english: "restaurant", sinhala: "ආපනශාලාව", pronunciation: "aa-puh-nuh-shaa-laa-vuh", spoken: { sinhala: "රෙස්ටුරන්ට්", pronunciation: "res-tu-runt" } },
      { english: "airport", sinhala: "ගුවන් තොටුපළ", pronunciation: "goo-vuhn thoh-too-puh-luh", spoken: { sinhala: "එයාපෝට්", pronunciation: "eh-yaa-poht" } },
      { english: "city", sinhala: "නගරය", pronunciation: "nuh-guh-ruh-yuh" },
      { english: "village", sinhala: "ගම", pronunciation: "guh-muh" },
      { english: "park", sinhala: "උද්‍යානය", pronunciation: "ood-yaa-nuh-yuh", spoken: { sinhala: "පාක්", pronunciation: "paak" } },
      { english: "library", sinhala: "පුස්තකාලය", pronunciation: "poos-thuh-kaa-luh-yuh" },
      { english: "office", sinhala: "කාර්යාලය", pronunciation: "kaar-yaa-luh-yuh", spoken: { sinhala: "ඔෆිස්", pronunciation: "oh-fis" } },
    ]
  },
  sriLankanCities: {
    name: "Sri Lankan Cities",
    nameSi: "ශ්‍රී ලාංකික නගර",
    icon: "🏙️",
    color: "#5D4037",
    words: [
      { english: "Colombo", sinhala: "කොළඹ", pronunciation: "koh-luhm-buh", example: "කොළඹ ශ්‍රී ලංකාවේ වාණිජ අගනගරයයි", exampleEn: "Colombo is the commercial capital of Sri Lanka" },
      { english: "Kandy", sinhala: "මහනුවර", pronunciation: "muh-huh-noo-vuh-ruh", example: "දළදා මාළිගාව මහනුවර තිබෙනවා", exampleEn: "Temple of Tooth is in Kandy" },
      { english: "Galle", sinhala: "ගාල්ල", pronunciation: "gaal-luh" },
      { english: "Jaffna", sinhala: "යාපනය", pronunciation: "yaa-puh-nuh-yuh" },
      { english: "Anuradhapura", sinhala: "අනුරාධපුරය", pronunciation: "uh-noo-raa-dhuh-poo-ruh-yuh" },
      { english: "Polonnaruwa", sinhala: "පොළොන්නරුව", pronunciation: "poh-lohn-nuh-roo-vuh" },
      { english: "Negombo", sinhala: "මීගමුව", pronunciation: "mee-guh-moo-vuh" },
      { english: "Trincomalee", sinhala: "ත්‍රිකුණාමලය", pronunciation: "three-koo-naa-muh-luh-yuh" },
      { english: "Batticaloa", sinhala: "මඩකලපුව", pronunciation: "muh-duh-kuh-luh-poo-vuh" },
      { english: "Matara", sinhala: "මාතර", pronunciation: "maa-thuh-ruh" },
      { english: "Kurunegala", sinhala: "කුරුණෑගල", pronunciation: "koo-roo-neh-guh-luh" },
      { english: "Ratnapura", sinhala: "රත්නපුරය", pronunciation: "ruhth-nuh-poo-ruh-yuh" },
      { english: "Nuwara Eliya", sinhala: "නුවරඑළිය", pronunciation: "noo-vuh-ruh-eh-lee-yuh" },
      { english: "Sigiriya", sinhala: "සීගිරිය", pronunciation: "see-gee-ree-yuh" },
      { english: "Ella", sinhala: "ඇල්ල", pronunciation: "ehl-luh" },
    ]
  },
  transport: {
    name: "Transport",
    nameSi: "ප්‍රවාහන",
    icon: "🚗",
    color: "#00BCD4",
    words: [
      { english: "car", sinhala: "මෝටර් රථය", pronunciation: "moh-tuhr ruh-thuh-yuh", spoken: { sinhala: "කාර්", pronunciation: "kaar" } },
      { english: "bus", sinhala: "බස් රථය", pronunciation: "buhs ruh-thuh-yuh", spoken: { sinhala: "බස්", pronunciation: "buhs" } },
      { english: "train", sinhala: "දුම්රිය", pronunciation: "doom-ree-yuh" },
      { english: "bicycle", sinhala: "බයිසිකලය", pronunciation: "bai-see-kuh-luh-yuh", spoken: { sinhala: "බයිසිකල්", pronunciation: "bai-see-kuhl" } },
      { english: "motorcycle", sinhala: "යතුරුපැදිය", pronunciation: "yuh-thoo-roo-peh-dee-yuh", spoken: { sinhala: "බයික්", pronunciation: "baik" } },
      { english: "airplane", sinhala: "ගුවන් යානය", pronunciation: "goo-vuhn yaa-nuh-yuh", spoken: { sinhala: "ප්ලේන්", pronunciation: "playn" } },
      { english: "boat", sinhala: "බෝට්ටුව", pronunciation: "boht-too-vuh" },
      { english: "ship", sinhala: "නැව", pronunciation: "neh-vuh" },
      { english: "tuk-tuk", sinhala: "ත්‍රිරෝද රථය", pronunciation: "three-roh-duh ruh-thuh-yuh", spoken: { sinhala: "තිරි වීල්", pronunciation: "thee-ree wheel" } },
      { english: "taxi", sinhala: "ටැක්සිය", pronunciation: "tehk-see-yuh" },
      { english: "truck", sinhala: "ලොරිය", pronunciation: "loh-ree-yuh" },
      { english: "helicopter", sinhala: "හෙලිකොප්ටරය", pronunciation: "heh-lee-kohp-tuh-ruh-yuh" },
    ]
  },
  professions: {
    name: "Professions",
    nameSi: "වෘත්තීන්",
    icon: "👨‍💼",
    color: "#3F51B5",
    words: [
      { english: "teacher", sinhala: "ගුරුවරයා", pronunciation: "goo-roo-vuh-ruh-yaa", spoken: { sinhala: "ටීචර්", pronunciation: "tee-chuhr" } },
      { english: "doctor", sinhala: "වෛද්‍යවරයා", pronunciation: "vaid-yuh-vuh-ruh-yaa", spoken: { sinhala: "ඩොක්ටර්", pronunciation: "dok-tuhr" } },
      { english: "nurse", sinhala: "හෙදිය", pronunciation: "heh-dee-yuh", spoken: { sinhala: "නර්ස්", pronunciation: "nurs" } },
      { english: "police", sinhala: "පොලිසිය", pronunciation: "poh-lee-see-yuh" },
      { english: "farmer", sinhala: "ගොවියා", pronunciation: "goh-vee-yaa" },
      { english: "driver", sinhala: "රියදුරා", pronunciation: "ree-yuh-doo-raa", spoken: { sinhala: "ඩ්‍රයිවර්", pronunciation: "drai-vuhr" } },
      { english: "engineer", sinhala: "ඉංජිනේරු", pronunciation: "in-jee-nay-roo" },
      { english: "lawyer", sinhala: "නීතිඥයා", pronunciation: "nee-theeg-nuh-yaa", spoken: { sinhala: "ලෝයර්", pronunciation: "loh-yuhr" } },
      { english: "singer", sinhala: "ගායකයා", pronunciation: "gaa-yuh-kuh-yaa" },
      { english: "dancer", sinhala: "නර්තකයා", pronunciation: "nuhr-thuh-kuh-yaa" },
      { english: "chef", sinhala: "පිසින්නා", pronunciation: "pee-sin-naa" },
      { english: "pilot", sinhala: "ගුවන් නියමුවා", pronunciation: "goo-vuhn nee-yuh-moo-vaa", spoken: { sinhala: "පයිලට්", pronunciation: "pai-luht" } },
      { english: "fisherman", sinhala: "ධීවරයා", pronunciation: "dhee-vuh-ruh-yaa" },
      { english: "artist", sinhala: "කලාකරුවා", pronunciation: "kuh-laa-kuh-roo-vaa" },
    ]
  },
  relationships: {
    name: "Relationships",
    nameSi: "සබඳතා",
    icon: "💑",
    color: "#E91E63",
    words: [
      { english: "friend", sinhala: "මිත්‍රයා", pronunciation: "mith-ruh-yaa", example: "ඔහු මගේ හොඳම යාළුවා", exampleEn: "He is my best friend", spoken: { sinhala: "යාළුවා", pronunciation: "yaa-loo-vaa" } },
      { english: "best friend", sinhala: "හොඳම මිත්‍රයා", pronunciation: "hohn-duh-muh mith-ruh-yaa", spoken: { sinhala: "හොඳම යාළුවා", pronunciation: "hohn-duh-muh yaa-loo-vaa" } },
      { english: "neighbor", sinhala: "අසල්වැසියා", pronunciation: "uh-suhl-veh-see-yaa" },
      { english: "colleague", sinhala: "සේවා සහකරු", pronunciation: "say-vaa suh-huh-kuh-roo" },
      { english: "boss", sinhala: "ප්‍රධානියා", pronunciation: "pruh-dhaa-nee-yaa", spoken: { sinhala: "බොස්", pronunciation: "bos" } },
      { english: "classmate", sinhala: "පන්ති මිතුරා", pronunciation: "puhn-thee mee-thoo-raa" },
      { english: "girlfriend", sinhala: "පෙම්වතිය", pronunciation: "pehm-vuh-thee-yuh", spoken: { sinhala: "ගර්ල්ෆ්‍රෙන්ඩ්", pronunciation: "gurl-frend" } },
      { english: "boyfriend", sinhala: "පෙම්වතා", pronunciation: "pehm-vuh-thaa", spoken: { sinhala: "බෝයිෆ්‍රෙන්ඩ්", pronunciation: "boy-frend" } },
      { english: "couple", sinhala: "යුවළ", pronunciation: "yoo-vuh-luh" },
      { english: "partner", sinhala: "සහකරු", pronunciation: "suh-huh-kuh-roo", spoken: { sinhala: "පාට්නර්", pronunciation: "paart-nuhr" } },
      { english: "enemy", sinhala: "සතුරා", pronunciation: "suh-thoo-raa" },
      { english: "stranger", sinhala: "නොදන්නා කෙනෙක්", pronunciation: "noh-duhn-naa keh-nehk" },
      { english: "guest", sinhala: "ආගන්තුකයා", pronunciation: "aa-guhn-thoo-kuh-yaa" },
      { english: "host", sinhala: "සත්කාරකයා", pronunciation: "suhth-kaa-ruh-kuh-yaa" },
    ]
  },
  time: {
    name: "Time & Days",
    nameSi: "කාලය සහ දින",
    icon: "⏰",
    color: "#FF5722",
    words: [
      { english: "today", sinhala: "අද", pronunciation: "uh-duh" },
      { english: "tomorrow", sinhala: "හෙට", pronunciation: "heh-tuh" },
      { english: "yesterday", sinhala: "ඊයේ", pronunciation: "ee-yay" },
      { english: "morning", sinhala: "උදේ", pronunciation: "oo-day" },
      { english: "afternoon", sinhala: "දවල්", pronunciation: "duh-vuhl" },
      { english: "evening", sinhala: "සවස", pronunciation: "suh-vuh-suh" },
      { english: "night", sinhala: "රාත්‍රිය", pronunciation: "raa-three-yuh", spoken: { sinhala: "රෑ", pronunciation: "rae" } },
      { english: "monday", sinhala: "සඳුදා", pronunciation: "suhn-doo-daa" },
      { english: "tuesday", sinhala: "අඟහරුවාදා", pronunciation: "uhn-guh-huh-roo-vaa-daa" },
      { english: "wednesday", sinhala: "බදාදා", pronunciation: "buh-daa-daa" },
      { english: "thursday", sinhala: "බ්‍රහස්පතින්දා", pronunciation: "bruh-huhs-puh-thin-daa" },
      { english: "friday", sinhala: "සිකුරාදා", pronunciation: "see-koo-raa-daa" },
      { english: "saturday", sinhala: "සෙනසුරාදා", pronunciation: "seh-nuh-soo-raa-daa" },
      { english: "sunday", sinhala: "ඉරිදා", pronunciation: "ee-ree-daa" },
    ]
  },
  monthsSeasons: {
    name: "Months & Seasons",
    nameSi: "මාස සහ සෘතු",
    icon: "📅",
    color: "#00ACC1",
    words: [
      { english: "January", sinhala: "ජනවාරි", pronunciation: "juh-nuh-vaa-ree" },
      { english: "February", sinhala: "පෙබරවාරි", pronunciation: "peh-buh-ruh-vaa-ree" },
      { english: "March", sinhala: "මාර්තු", pronunciation: "maar-thoo" },
      { english: "April", sinhala: "අප්‍රේල්", pronunciation: "uhp-rayl" },
      { english: "May", sinhala: "මැයි", pronunciation: "meh-yee" },
      { english: "June", sinhala: "ජූනි", pronunciation: "joo-nee" },
      { english: "July", sinhala: "ජූලි", pronunciation: "joo-lee" },
      { english: "August", sinhala: "අගෝස්තු", pronunciation: "uh-gohs-thoo" },
      { english: "September", sinhala: "සැප්තැම්බර්", pronunciation: "sehp-thehm-buhr" },
      { english: "October", sinhala: "ඔක්තෝබර්", pronunciation: "ohk-thoh-buhr" },
      { english: "November", sinhala: "නොවැම්බර්", pronunciation: "noh-vehm-buhr" },
      { english: "December", sinhala: "දෙසැම්බර්", pronunciation: "deh-sehm-buhr" },
      { english: "monsoon", sinhala: "මෝසම", pronunciation: "moh-suh-muh" },
      { english: "dry season", sinhala: "වියළි කාලය", pronunciation: "vee-yuh-lee kaa-luh-yuh" },
      { english: "rainy season", sinhala: "වැසි කාලය", pronunciation: "veh-see kaa-luh-yuh" },
      { english: "summer", sinhala: "ග්‍රීෂ්ම", pronunciation: "gree-shmuh" },
    ]
  },
  weather: {
    name: "Weather",
    nameSi: "කාලගුණය",
    icon: "🌤️",
    color: "#03A9F4",
    words: [
      { english: "hot", sinhala: "උණුසුම්", pronunciation: "oo-noo-soom" },
      { english: "cold", sinhala: "සීතල", pronunciation: "see-thuh-luh" },
      { english: "rain", sinhala: "වැස්ස", pronunciation: "vehs-suh" },
      { english: "sunny", sinhala: "අව්ව", pronunciation: "uhv-vuh" },
      { english: "cloudy", sinhala: "වලාකුළු", pronunciation: "vuh-laa-koo-loo" },
      { english: "windy", sinhala: "සුළං", pronunciation: "soo-luhn" },
      { english: "storm", sinhala: "කුණාටුව", pronunciation: "koo-naa-too-vuh" },
      { english: "thunder", sinhala: "ගිගුරුම", pronunciation: "gee-goo-roo-muh" },
      { english: "lightning", sinhala: "අකුණ", pronunciation: "uh-koo-nuh" },
      { english: "fog", sinhala: "මීදුම", pronunciation: "mee-doo-muh" },
      { english: "snow", sinhala: "හිම", pronunciation: "hee-muh" },
    ]
  },
  emergencyPhrases: {
    name: "Emergency Phrases",
    nameSi: "හදිසි වාක්‍ය",
    icon: "🚨",
    color: "#D32F2F",
    words: [
      { english: "Help!", sinhala: "උදව්!", pronunciation: "oo-duhv", example: "උදව්! මට උදව් අවශ්‍යයි!", exampleEn: "Help! I need help!" },
      { english: "Call the police!", sinhala: "පොලිසියට කතා කරන්න!", pronunciation: "poh-lee-see-yuh-tuh kuh-thaa kuh-ruhn-nuh" },
      { english: "Call an ambulance!", sinhala: "ගිලන් රථයක් කතා කරන්න!", pronunciation: "gee-luhn ruh-thuh-yuhk kuh-thaa kuh-ruhn-nuh" },
      { english: "I need a doctor", sinhala: "මට වෛද්‍යවරයෙක් අවශ්‍යයි", pronunciation: "muh-tuh vaid-yuh-vuh-ruh-yehk uh-vuh-shuh-yai" },
      { english: "Where is the hospital?", sinhala: "රෝහල කොහේද?", pronunciation: "roh-huh-luh koh-hay-duh" },
      { english: "I am lost", sinhala: "මම මග හැරුණා", pronunciation: "muh-muh muh-guh heh-roo-naa" },
      { english: "Fire!", sinhala: "ගිනි!", pronunciation: "gee-nee" },
      { english: "Stop!", sinhala: "නවත්වන්න!", pronunciation: "nuh-vuhth-vuhn-nuh" },
      { english: "Be careful!", sinhala: "පරිස්සම්!", pronunciation: "puh-ris-suhm" },
      { english: "I am hurt", sinhala: "මට තුවාල වුණා", pronunciation: "muh-tuh thoo-vaa-luh voo-naa" },
      { english: "I feel sick", sinhala: "මට අසනීපයි", pronunciation: "muh-tuh uh-suh-nee-puh-yee" },
      { english: "Danger!", sinhala: "අනතුර!", pronunciation: "uh-nuh-thoo-ruh" },
      { english: "Embassy", sinhala: "තානාපති කාර්යාලය", pronunciation: "thaa-naa-puh-thee kaar-yaa-luh-yuh" },
      { english: "I lost my passport", sinhala: "මගේ විදේශ ගමන් බලපත්‍රය නැති වුණා", pronunciation: "muh-gay vee-day-shuh guh-muhn buh-luh-puth-ruh-yuh neh-thee voo-naa" },
    ]
  },
  travelPhrases: {
    name: "Travel Phrases",
    nameSi: "සංචාරක වාක්‍ය",
    icon: "✈️",
    color: "#1976D2",
    words: [
      { english: "How much is this?", sinhala: "මේක කීයද?", pronunciation: "may-kuh kee-yuh-duh", example: "මේ තෙල් එක කීයද?", exampleEn: "How much is this oil?" },
      { english: "Where is...?", sinhala: "...කොහේද?", pronunciation: "koh-hay-duh" },
      { english: "I want to go to...", sinhala: "මට ... යන්න ඕනෑ", pronunciation: "muh-tuh ... yuhn-nuh oh-neh" },
      { english: "Ticket please", sinhala: "ටිකට් එකක් දෙන්න", pronunciation: "tee-kuht eh-kuhk dehn-nuh" },
      { english: "One ticket", sinhala: "ටිකට් එකක්", pronunciation: "tee-kuht eh-kuhk" },
      { english: "Two tickets", sinhala: "ටිකට් දෙකක්", pronunciation: "tee-kuht deh-kuhk" },
      { english: "Do you speak English?", sinhala: "ඔබ ඉංග්‍රීසි කතා කරනවද?", pronunciation: "oh-buh in-gree-see kuh-thaa kuh-ruh-nuh-vuh-duh" },
      { english: "I don't understand", sinhala: "මට තේරෙන්නේ නැහැ", pronunciation: "muh-tuh thay-rehn-nay neh-heh" },
      { english: "Can you help me?", sinhala: "ඔබට මට උදව් කරන්න පුළුවන්ද?", pronunciation: "oh-buh-tuh muh-tuh oo-duhv kuh-ruhn-nuh poo-loo-vuhn-duh" },
      { english: "Where is the toilet?", sinhala: "වැසිකිළිය කොහේද?", pronunciation: "veh-see-kee-lee-yuh koh-hay-duh" },
      { english: "I am a tourist", sinhala: "මම සංචාරකයෙක්", pronunciation: "muh-muh suhn-chaa-ruh-kuh-yehk" },
      { english: "Hotel", sinhala: "හෝටලය", pronunciation: "hoh-tuh-luh-yuh" },
      { english: "Restaurant", sinhala: "ආපනශාලාව", pronunciation: "aa-puh-nuh-shaa-laa-vuh" },
      { english: "Bus station", sinhala: "බස් නැවතුම්පොළ", pronunciation: "buhs neh-vuh-thoom-poh-luh" },
      { english: "Train station", sinhala: "දුම්රිය ස්ථානය", pronunciation: "doom-ree-yuh sthaa-nuh-yuh" },
      { english: "Too expensive", sinhala: "වැඩිය මිලයි", pronunciation: "veh-dee-yuh mee-luh-yee" },
      { english: "Cheaper please", sinhala: "අඩු කරන්න", pronunciation: "uh-doo kuh-ruhn-nuh" },
    ]
  },
  materials: {
    name: "Materials",
    nameSi: "ද්‍රව්‍ය",
    icon: "🪨",
    color: "#78909C",
    words: [
      { english: "wood", sinhala: "ලී", pronunciation: "lee" },
      { english: "metal", sinhala: "ලෝහ", pronunciation: "loh-huh" },
      { english: "glass", sinhala: "වීදුරු", pronunciation: "vee-doo-roo" },
      { english: "plastic", sinhala: "ප්ලාස්ටික්", pronunciation: "plaas-tik" },
      { english: "paper", sinhala: "කඩදාසි", pronunciation: "kuh-duh-daa-see" },
      { english: "cloth", sinhala: "රෙද්ද", pronunciation: "rehd-duh" },
      { english: "leather", sinhala: "සම්", pronunciation: "suhm" },
      { english: "rubber", sinhala: "රබර්", pronunciation: "ruh-buhr" },
      { english: "stone", sinhala: "ගල", pronunciation: "guh-luh" },
      { english: "gold", sinhala: "රන්", pronunciation: "ruhn" },
      { english: "silver", sinhala: "රිදී", pronunciation: "ree-dee" },
      { english: "iron", sinhala: "යකඩ", pronunciation: "yuh-kuh-duh" },
      { english: "cotton", sinhala: "කපු", pronunciation: "kuh-poo" },
      { english: "silk", sinhala: "සේද", pronunciation: "say-duh" },
      { english: "cement", sinhala: "සිමෙන්ති", pronunciation: "see-mehn-thee" },
      { english: "brick", sinhala: "ගඩොල්", pronunciation: "guh-dohl" },
    ]
  },
  tools: {
    name: "Tools",
    nameSi: "මෙවලම්",
    icon: "🔧",
    color: "#455A64",
    words: [
      { english: "hammer", sinhala: "මිටිය", pronunciation: "mee-tee-yuh" },
      { english: "screwdriver", sinhala: "ඉස්කුරුප්පු නියනය", pronunciation: "is-koo-roop-poo nee-yuh-nuh-yuh" },
      { english: "saw", sinhala: "කියත", pronunciation: "kee-yuh-thuh" },
      { english: "drill", sinhala: "විදින යන්ත්‍රය", pronunciation: "vee-deen yuhn-thruh-yuh" },
      { english: "nail", sinhala: "ඇණ", pronunciation: "eh-nuh" },
      { english: "screw", sinhala: "ඉස්කුරුප්පු", pronunciation: "is-koo-roop-poo" },
      { english: "pliers", sinhala: "ප්ලයර්", pronunciation: "plai-yuhr" },
      { english: "wrench", sinhala: "රෙන්චිය", pronunciation: "rehn-chee-yuh" },
      { english: "axe", sinhala: "පොරව", pronunciation: "poh-ruh-vuh" },
      { english: "shovel", sinhala: "උදැල්ල", pronunciation: "oo-dehl-luh" },
      { english: "ladder", sinhala: "ඉනිම", pronunciation: "ee-nee-muh" },
      { english: "rope", sinhala: "කඹය", pronunciation: "kuhm-buh-yuh" },
      { english: "bucket", sinhala: "බාල්දිය", pronunciation: "baal-dee-yuh" },
      { english: "broom", sinhala: "ඉඹුල", pronunciation: "im-boo-luh" },
    ]
  },
  slangInformal: {
    name: "Slang & Informal",
    nameSi: "කතා බස",
    icon: "😎",
    color: "#7B1FA2",
    words: [
      { english: "cool/great", sinhala: "මරු", pronunciation: "muh-roo", example: "ඒක මරු!", exampleEn: "That's cool!" },
      { english: "awesome", sinhala: "සුපිරි", pronunciation: "soo-pee-ree" },
      { english: "buddy/dude", sinhala: "මචං", pronunciation: "muh-chuhn" },
      { english: "what's up", sinhala: "මොකද වෙන්නේ", pronunciation: "moh-kuh-duh vehn-nay" },
      { english: "chill/relax", sinhala: "රිලැක්ස්", pronunciation: "ree-lehks" },
      { english: "no way", sinhala: "බෑ කොහොමත්", pronunciation: "beh koh-hoh-muhth" },
      { english: "really?", sinhala: "ඇත්තද?", pronunciation: "ehth-thuh-duh" },
      { english: "crazy", sinhala: "පිස්සු", pronunciation: "pis-soo" },
      { english: "boring", sinhala: "කම්මැලි", pronunciation: "kuhm-meh-lee" },
      { english: "delicious", sinhala: "සුපිරි රසයි", pronunciation: "soo-pee-ree ruh-suh-yee" },
      { english: "hurry up", sinhala: "ඉක්මන් කරපන්", pronunciation: "ik-muhn kuh-ruh-puhn" },
      { english: "forget it", sinhala: "ඒක දාපන්", pronunciation: "ay-kuh daa-puhn" },
      { english: "never mind", sinhala: "කමක් නෑ", pronunciation: "kuh-muhk neh" },
      { english: "let's go", sinhala: "යමු", pronunciation: "yuh-moo" },
      { english: "shut up (casual)", sinhala: "කට වහගන්", pronunciation: "kuh-tuh vuh-huh-guhn" },
    ]
  },
  opposites: {
    name: "Opposites",
    nameSi: "විරුද්ධ පද",
    icon: "↔️",
    color: "#FF7043",
    words: [
      { english: "big / small", sinhala: "ලොකු / පොඩි", pronunciation: "loh-koo / poh-dee" },
      { english: "hot / cold", sinhala: "උණුසුම් / සීතල", pronunciation: "oo-noo-soom / see-thuh-luh" },
      { english: "fast / slow", sinhala: "වේගවත් / සෙමින්", pronunciation: "vay-guh-vuhth / seh-min" },
      { english: "good / bad", sinhala: "හොඳ / නරක", pronunciation: "hohn-duh / nuh-ruh-kuh" },
      { english: "new / old", sinhala: "අලුත් / පරණ", pronunciation: "uh-looth / puh-ruh-nuh" },
      { english: "happy / sad", sinhala: "සතුටු / දුකට", pronunciation: "suh-thoo-too / doo-kuh-tuh" },
      { english: "tall / short", sinhala: "උස / මිටි", pronunciation: "oo-suh / mee-tee" },
      { english: "heavy / light", sinhala: "බර / සැහැල්ලු", pronunciation: "buh-ruh / seh-hehl-loo" },
      { english: "easy / difficult", sinhala: "ලේසි / අමාරු", pronunciation: "lay-see / uh-maa-roo" },
      { english: "open / close", sinhala: "අරින්න / වහන්න", pronunciation: "uh-rin-nuh / vuh-huhn-nuh" },
      { english: "day / night", sinhala: "දවස / රෑ", pronunciation: "duh-vuh-suh / reh" },
      { english: "up / down", sinhala: "ඉහළ / පහළ", pronunciation: "ee-huh-luh / puh-huh-luh" },
      { english: "in / out", sinhala: "ඇතුළ / පිට", pronunciation: "eh-thoo-luh / pee-tuh" },
      { english: "full / empty", sinhala: "පිරුණු / හිස්", pronunciation: "pee-roo-noo / his" },
      { english: "clean / dirty", sinhala: "පිරිසිදු / අපිරිසිදු", pronunciation: "pee-ree-see-doo / uh-pee-ree-see-doo" },
      { english: "rich / poor", sinhala: "ධනවත් / දුප්පත්", pronunciation: "dhuh-nuh-vuhth / doop-puhth" },
    ]
  },
  technology: {
    name: "Technology",
    nameSi: "තාක්ෂණය",
    icon: "💻",
    color: "#673AB7",
    words: [
      { english: "computer", sinhala: "පරිගණකය", pronunciation: "puh-ree-guh-nuh-kuh-yuh", spoken: { sinhala: "කොම්පියුටර්", pronunciation: "kohm-pew-tuhr" } },
      { english: "phone", sinhala: "දුරකථනය", pronunciation: "doo-ruh-kuh-thuh-nuh-yuh", spoken: { sinhala: "ෆෝන්", pronunciation: "fohn" } },
      { english: "television", sinhala: "රූපවාහිනිය", pronunciation: "roo-puh-vaa-hee-nee-yuh", spoken: { sinhala: "ටීවී", pronunciation: "tee-vee" } },
      { english: "internet", sinhala: "අන්තර්ජාලය", pronunciation: "uhn-thuhr-jaa-luh-yuh", spoken: { sinhala: "ඉන්ටනෙට්", pronunciation: "in-tuh-net" } },
      { english: "email", sinhala: "විද්‍යුත් තැපෑල", pronunciation: "vid-yooth theh-peh-luh", spoken: { sinhala: "ඊමේල්", pronunciation: "ee-mayl" } },
      { english: "camera", sinhala: "කැමරාව", pronunciation: "keh-muh-raa-vuh" },
      { english: "laptop", sinhala: "ලැප්ටොප්", pronunciation: "lehp-tohp" },
      { english: "keyboard", sinhala: "යතුරු පුවරුව", pronunciation: "yuh-thoo-roo poo-vuh-roo-vuh" },
      { english: "mouse", sinhala: "මවුසය", pronunciation: "mau-suh-yuh" },
      { english: "printer", sinhala: "මුද්‍රණ යන්ත්‍රය", pronunciation: "mood-ruh-nuh yuhn-thruh-yuh", spoken: { sinhala: "ප්‍රින්ටර්", pronunciation: "prin-tuhr" } },
      { english: "radio", sinhala: "ගුවන් විදුලිය", pronunciation: "goo-vuhn vee-doo-lee-yuh", spoken: { sinhala: "රේඩියෝ", pronunciation: "ray-dee-yoh" } },
    ]
  },
  household: {
    name: "Household",
    nameSi: "ගෘහ භාණ්ඩ",
    icon: "🏠",
    color: "#8BC34A",
    words: [
      { english: "table", sinhala: "මේසය", pronunciation: "may-suh-yuh" },
      { english: "chair", sinhala: "පුටුව", pronunciation: "poo-too-vuh" },
      { english: "bed", sinhala: "ඇඳ", pronunciation: "ehn-duh" },
      { english: "door", sinhala: "දොර", pronunciation: "doh-ruh" },
      { english: "window", sinhala: "ජනේලය", pronunciation: "juh-nay-luh-yuh" },
      { english: "lamp", sinhala: "ලාම්පුව", pronunciation: "laam-poo-vuh" },
      { english: "fan", sinhala: "පංකාව", pronunciation: "puhn-kaa-vuh", spoken: { sinhala: "ෆෑන්", pronunciation: "faen" } },
      { english: "mirror", sinhala: "කණ්ණාඩිය", pronunciation: "kuhn-naa-dee-yuh" },
      { english: "clock", sinhala: "ඔරලෝසුව", pronunciation: "oh-ruh-loh-soo-vuh" },
      { english: "pillow", sinhala: "කොට්ටය", pronunciation: "koht-tuh-yuh" },
      { english: "blanket", sinhala: "බ්ලැන්කට්", pronunciation: "blehn-kuht" },
      { english: "towel", sinhala: "තුවාය", pronunciation: "thoo-vaa-yuh" },
      { english: "soap", sinhala: "සබන්", pronunciation: "suh-buhn" },
      { english: "knife", sinhala: "පිහිය", pronunciation: "pee-hee-yuh" },
      { english: "spoon", sinhala: "හැන්ද", pronunciation: "hehn-duh" },
      { english: "plate", sinhala: "පිඟානය", pronunciation: "pin-gaa-nuh-yuh" },
      { english: "cup", sinhala: "කෝප්පය", pronunciation: "kohp-puh-yuh" },
    ]
  },
  clothing: {
    name: "Clothing",
    nameSi: "ඇඳුම්",
    icon: "👔",
    color: "#E91E63",
    words: [
      { english: "shirt", sinhala: "කමිසය", pronunciation: "kuh-mee-suh-yuh", spoken: { sinhala: "ෂර්ට්", pronunciation: "shurt" } },
      { english: "pants", sinhala: "කලිසම", pronunciation: "kuh-lee-suh-muh" },
      { english: "dress", sinhala: "ගවුම", pronunciation: "guh-voo-muh", spoken: { sinhala: "ඩ්‍රෙස්", pronunciation: "dres" } },
      { english: "shoes", sinhala: "සපත්තු", pronunciation: "suh-puhth-thoo", spoken: { sinhala: "ෂූස්", pronunciation: "shooz" } },
      { english: "hat", sinhala: "තොප්පිය", pronunciation: "thohp-pee-yuh", spoken: { sinhala: "හැට්", pronunciation: "haet" } },
      { english: "jacket", sinhala: "ජැකට්", pronunciation: "jeh-kuht" },
      { english: "jeans", sinhala: "ජීන්ස්", pronunciation: "jeens" },
      { english: "skirt", sinhala: "සාය", pronunciation: "saa-yuh" },
      { english: "socks", sinhala: "මේස්", pronunciation: "mays", spoken: { sinhala: "සොක්ස්", pronunciation: "soks" } },
      { english: "tie", sinhala: "ටයි", pronunciation: "tai" },
      { english: "belt", sinhala: "පටිය", pronunciation: "puh-tee-yuh", spoken: { sinhala: "බෙල්ට්", pronunciation: "belt" } },
      { english: "glasses", sinhala: "කණ්ණාඩි", pronunciation: "kuhn-naa-dee" },
      { english: "watch", sinhala: "ඔරලෝසුව", pronunciation: "oh-ruh-loh-soo-vuh", spoken: { sinhala: "වොච්", pronunciation: "woch" } },
      { english: "umbrella", sinhala: "කුඩය", pronunciation: "koo-duh-yuh" },
    ]
  },
  verbs: {
    name: "Common Verbs",
    nameSi: "ක්‍රියා පද",
    icon: "🏃",
    color: "#009688",
    words: [
      { english: "eat", sinhala: "කනවා", pronunciation: "kuh-nuh-vaa", example: "මම බත් කනවා", exampleEn: "I eat rice" },
      { english: "drink", sinhala: "බොනවා", pronunciation: "boh-nuh-vaa" },
      { english: "sleep", sinhala: "නිදාගන්නවා", pronunciation: "nee-daa-guhn-nuh-vaa" },
      { english: "walk", sinhala: "යනවා", pronunciation: "yuh-nuh-vaa" },
      { english: "run", sinhala: "දුවනවා", pronunciation: "doo-vuh-nuh-vaa" },
      { english: "read", sinhala: "කියවනවා", pronunciation: "kee-yuh-vuh-nuh-vaa" },
      { english: "write", sinhala: "ලියනවා", pronunciation: "lee-yuh-nuh-vaa" },
      { english: "speak", sinhala: "කතා කරනවා", pronunciation: "kuh-thaa kuh-ruh-nuh-vaa" },
      { english: "listen", sinhala: "අහනවා", pronunciation: "uh-huh-nuh-vaa" },
      { english: "see", sinhala: "බලනවා", pronunciation: "buh-luh-nuh-vaa" },
      { english: "come", sinhala: "එනවා", pronunciation: "eh-nuh-vaa" },
      { english: "go", sinhala: "යනවා", pronunciation: "yuh-nuh-vaa" },
      { english: "sit", sinhala: "වාඩි වෙනවා", pronunciation: "vaa-dee veh-nuh-vaa" },
      { english: "stand", sinhala: "නැගිටිනවා", pronunciation: "neh-gee-tee-nuh-vaa" },
      { english: "love", sinhala: "ආදරය කරනවා", pronunciation: "aa-duh-ruh-yuh kuh-ruh-nuh-vaa" },
      { english: "think", sinhala: "හිතනවා", pronunciation: "hee-thuh-nuh-vaa" },
      { english: "know", sinhala: "දන්නවා", pronunciation: "duhn-nuh-vaa" },
      { english: "give", sinhala: "දෙනවා", pronunciation: "deh-nuh-vaa" },
      { english: "take", sinhala: "ගන්නවා", pronunciation: "guhn-nuh-vaa" },
    ]
  },
  adjectives: {
    name: "Adjectives",
    nameSi: "විශේෂණ",
    icon: "✨",
    color: "#FFC107",
    words: [
      { english: "big", sinhala: "ලොකු", pronunciation: "loh-koo" },
      { english: "small", sinhala: "පොඩි", pronunciation: "poh-dee" },
      { english: "good", sinhala: "හොඳ", pronunciation: "hohn-duh" },
      { english: "bad", sinhala: "නරක", pronunciation: "nuh-ruh-kuh" },
      { english: "beautiful", sinhala: "ලස්සන", pronunciation: "luhs-suh-nuh" },
      { english: "happy", sinhala: "සතුටු", pronunciation: "suh-thoo-too" },
      { english: "sad", sinhala: "දුකට", pronunciation: "doo-kuh-tuh" },
      { english: "new", sinhala: "අලුත්", pronunciation: "uh-looth" },
      { english: "old", sinhala: "පරණ", pronunciation: "puh-ruh-nuh" },
      { english: "fast", sinhala: "වේගවත්", pronunciation: "vay-guh-vuhth" },
      { english: "slow", sinhala: "සෙමින්", pronunciation: "seh-min" },
      { english: "hot", sinhala: "උණුසුම්", pronunciation: "oo-noo-soom" },
      { english: "cold", sinhala: "සීතල", pronunciation: "see-thuh-luh" },
      { english: "tall", sinhala: "උස", pronunciation: "oo-suh" },
      { english: "short", sinhala: "මිටි", pronunciation: "mee-tee" },
      { english: "strong", sinhala: "ශක්තිමත්", pronunciation: "shuhk-thee-muhth" },
      { english: "kind", sinhala: "කරුණාවන්ත", pronunciation: "kuh-roo-naa-vuhn-thuh" },
    ]
  },
  emotions: {
    name: "Emotions",
    nameSi: "හැඟීම්",
    icon: "😊",
    color: "#FF4081",
    words: [
      { english: "happy", sinhala: "සතුටු", pronunciation: "suh-thoo-too" },
      { english: "sad", sinhala: "දුකට", pronunciation: "doo-kuh-tuh" },
      { english: "angry", sinhala: "තරහ", pronunciation: "thuh-ruh-huh" },
      { english: "afraid", sinhala: "බය", pronunciation: "buh-yuh" },
      { english: "surprised", sinhala: "පුදුම", pronunciation: "poo-doo-muh" },
      { english: "tired", sinhala: "වෙහෙසුණු", pronunciation: "veh-heh-soo-noo" },
      { english: "hungry", sinhala: "බඩගිනි", pronunciation: "buh-duh-gee-nee" },
      { english: "thirsty", sinhala: "පිපාසය", pronunciation: "pee-paa-suh-yuh" },
      { english: "excited", sinhala: "උද්යෝගිමත්", pronunciation: "ood-yoh-gee-muhth" },
      { english: "bored", sinhala: "කම්මැලි", pronunciation: "kuhm-meh-lee" },
      { english: "proud", sinhala: "ආඩම්බර", pronunciation: "aa-duhm-buh-ruh" },
      { english: "shy", sinhala: "ලැජ්ජා", pronunciation: "lehj-jaa" },
      { english: "love", sinhala: "ආදරය", pronunciation: "aa-duh-ruh-yuh" },
      { english: "peace", sinhala: "සාමය", pronunciation: "saa-muh-yuh" },
      { english: "hope", sinhala: "බලාපොරොත්තුව", pronunciation: "buh-laa-poh-rohth-thoo-vuh" },
    ]
  },
  directions: {
    name: "Directions",
    nameSi: "දිශාවන්",
    icon: "🧭",
    color: "#1976D2",
    words: [
      { english: "left", sinhala: "වම", pronunciation: "vuh-muh" },
      { english: "right", sinhala: "දකුණ", pronunciation: "duh-koo-nuh" },
      { english: "straight", sinhala: "කෙලින්ම", pronunciation: "keh-lin-muh" },
      { english: "up", sinhala: "ඉහළට", pronunciation: "ee-huh-luh-tuh" },
      { english: "down", sinhala: "පහළට", pronunciation: "puh-huh-luh-tuh" },
      { english: "front", sinhala: "ඉදිරිය", pronunciation: "ee-dee-ree-yuh" },
      { english: "back", sinhala: "පිටුපස", pronunciation: "pee-too-puh-suh" },
      { english: "north", sinhala: "උතුර", pronunciation: "oo-thoo-ruh" },
      { english: "south", sinhala: "දකුණ", pronunciation: "duh-koo-nuh" },
      { english: "east", sinhala: "නැගෙනහිර", pronunciation: "neh-geh-nuh-hee-ruh" },
      { english: "west", sinhala: "බටහිර", pronunciation: "buh-tuh-hee-ruh" },
      { english: "near", sinhala: "ළඟ", pronunciation: "luh-nguh" },
      { english: "far", sinhala: "දුර", pronunciation: "doo-ruh" },
      { english: "here", sinhala: "මෙතන", pronunciation: "meh-thuh-nuh" },
      { english: "there", sinhala: "එතන", pronunciation: "eh-thuh-nuh" },
      { english: "where", sinhala: "කොහේද", pronunciation: "koh-hay-duh" },
    ]
  },
  questions: {
    name: "Question Words",
    nameSi: "ප්‍රශ්න වචන",
    icon: "❓",
    color: "#7B1FA2",
    words: [
      { english: "what", sinhala: "මොකද", pronunciation: "moh-kuh-duh" },
      { english: "who", sinhala: "කවුද", pronunciation: "kuh-voo-duh" },
      { english: "where", sinhala: "කොහේද", pronunciation: "koh-hay-duh" },
      { english: "when", sinhala: "කවදාද", pronunciation: "kuh-vuh-daa-duh" },
      { english: "why", sinhala: "ඇයි", pronunciation: "eh-yee" },
      { english: "how", sinhala: "කොහොමද", pronunciation: "koh-hoh-muh-duh" },
      { english: "how much", sinhala: "කීයද", pronunciation: "kee-yuh-duh" },
      { english: "how many", sinhala: "කීයක්ද", pronunciation: "kee-yuhk-duh" },
      { english: "which", sinhala: "කුමක්ද", pronunciation: "koo-muhk-duh" },
      { english: "whose", sinhala: "කාගේද", pronunciation: "kaa-gay-duh" },
      { english: "can", sinhala: "පුළුවන්ද", pronunciation: "poo-loo-vuhn-duh" },
      { english: "is there", sinhala: "තියෙනවද", pronunciation: "thee-yeh-nuh-vuh-duh" },
      { english: "do you have", sinhala: "ඔබට තියෙනවද", pronunciation: "oh-buh-tuh thee-yeh-nuh-vuh-duh" },
    ]
  },
  commonPhrases: {
    name: "Common Phrases",
    nameSi: "පොදු වාක්‍ය",
    icon: "💬",
    color: "#00897B",
    words: [
      { english: "I don't understand", sinhala: "මට තේරෙන්නේ නැහැ", pronunciation: "muh-tuh thay-rehn-nay neh-heh" },
      { english: "please repeat", sinhala: "කරුණාකර නැවත කියන්න", pronunciation: "kuh-roo-naa-kuh-ruh neh-vuh-thuh kee-yuhn-nuh", spoken: { sinhala: "ආයෙත් කියන්නකෝ", pronunciation: "aa-yeth kee-yuhn-nuh-koh" } },
      { english: "speak slowly", sinhala: "සෙමින් කතා කරන්න", pronunciation: "seh-min kuh-thaa kuh-ruhn-nuh" },
      { english: "I am learning", sinhala: "මම ඉගෙන ගන්නවා", pronunciation: "muh-muh ee-geh-nuh guhn-nuh-vaa" },
      { english: "what is this", sinhala: "මේක මොකද", pronunciation: "may-kuh moh-kuh-duh" },
      { english: "how are you", sinhala: "කොහොමද", pronunciation: "koh-hoh-muh-duh" },
      { english: "I am fine", sinhala: "මම හොඳින්", pronunciation: "muh-muh hohn-din" },
      { english: "nice to meet you", sinhala: "ඔබව හමුවීම සතුටක්", pronunciation: "oh-buh-vuh huh-moo-vee-muh suh-thoo-tuhk", spoken: { sinhala: "ඔයාව හම්බවීම සතුටක්", pronunciation: "oh-yaa-vuh huhm-buh-vee-muh suh-thoo-tuhk" } },
      { english: "see you later", sinhala: "පසුව හමුවෙමු", pronunciation: "puh-soo-vuh huh-moo-veh-moo", spoken: { sinhala: "පස්සේ හම්බවෙමු", pronunciation: "puhs-say huhm-buh-veh-moo" } },
      { english: "take care", sinhala: "ප්‍රවේසම්", pronunciation: "pruh-vay-suhm" },
      { english: "excuse me", sinhala: "සමාවන්න", pronunciation: "suh-maa-vuhn-nuh" },
      { english: "I love you", sinhala: "මම ඔයාට ආදරෙයි", pronunciation: "muh-muh oh-yaa-tuh aa-duh-reh-yee" },
      { english: "happy birthday", sinhala: "සුභ උපන්දිනයක්", pronunciation: "soo-bhuh oo-puhn-dee-nuh-yuhk" },
      { english: "congratulations", sinhala: "සුභ පැතුම්", pronunciation: "soo-bhuh peh-thoom" },
    ]
  },
};

// Get all words for search and quiz
const getAllWords = () => {
  const allWords = [];
  Object.keys(categories).forEach(catKey => {
    categories[catKey].words.forEach(word => {
      allWords.push({ ...word, category: catKey, categoryName: categories[catKey].name });
    });
  });
  return allWords;
};

// Word of the day - based on date
const getWordOfTheDay = () => {
  const allWords = getAllWords();
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  return allWords[dayOfYear % allWords.length];
};

const Dictionary = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [playingWord, setPlayingWord] = useState(null);
  const [playingSpoken, setPlayingSpoken] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('dictionaryFavorites');
    return saved ? JSON.parse(saved) : [];
  });
  const [showFavorites, setShowFavorites] = useState(false);
  const [quizMode, setQuizMode] = useState(false);
  const [quizState, setQuizState] = useState({
    currentQuestion: 0,
    score: 0,
    questions: [],
    answered: false,
    selectedAnswer: null,
  });
  const [showExamples, setShowExamples] = useState({});

  const wordOfTheDay = useMemo(() => getWordOfTheDay(), []);
  const allWords = useMemo(() => getAllWords(), []);

  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('dictionaryFavorites', JSON.stringify(favorites));
  }, [favorites]);

  const handleSpeak = async (text, index) => {
    console.log("=== DICTIONARY MAIN SPEAK ===");
    console.log("Text:", text);
    console.log("Index:", index);
    console.log("Audio path:", soundService.getWordAudioPath(text));

    if (playingWord === index) {
      soundService.stop();
      setPlayingWord(null);
      return;
    }

    setPlayingWord(index);
    setPlayingSpoken(null);
    try {
      await soundService.speakSinhalaWord(text);
      console.log("Dictionary audio completed");
    } catch (error) {
      console.error('Audio playback error:', error);
    } finally {
      setPlayingWord(null);
    }
  };

  const handleSpeakSpoken = async (e, text, index) => {
    e.stopPropagation();
    e.preventDefault();
    console.log('=== SPOKEN WORD CLICK ===');
    console.log('Text:', text);
    console.log('Index:', index);
    console.log('Audio path:', soundService.getWordAudioPath(text));

    if (playingSpoken === index) {
      soundService.stop();
      setPlayingSpoken(null);
      return;
    }

    setPlayingSpoken(index);
    setPlayingWord(null);
    try {
      console.log('Calling speakSinhalaWord...');
      await soundService.speakSinhalaWord(text);
      console.log('speakSinhalaWord completed');
    } catch (error) {
      console.error('Audio playback error:', error);
    } finally {
      setPlayingSpoken(null);
    }
  };

  const toggleFavorite = (word) => {
    const key = `${word.english}-${word.sinhala}`;
    setFavorites(prev => {
      if (prev.includes(key)) {
        return prev.filter(f => f !== key);
      }
      return [...prev, key];
    });
  };

  const isFavorite = (word) => {
    return favorites.includes(`${word.english}-${word.sinhala}`);
  };

  const toggleExample = (index) => {
    setShowExamples(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // Search functionality
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return allWords.filter(word =>
      word.english.toLowerCase().includes(query) ||
      word.sinhala.includes(query) ||
      word.pronunciation.toLowerCase().includes(query)
    ).slice(0, 20);
  }, [searchQuery, allWords]);

  // Get favorite words
  const favoriteWords = useMemo(() => {
    return allWords.filter(word => isFavorite(word));
  }, [favorites, allWords]);

  // Quiz functionality
  const startQuiz = (categoryKey = null) => {
    let wordsPool = categoryKey ? categories[categoryKey].words : allWords;
    if (wordsPool.length < 4) {
      alert('Not enough words for quiz. Need at least 4 words.');
      return;
    }

    // Shuffle and pick 10 questions
    const shuffled = [...wordsPool].sort(() => Math.random() - 0.5);
    const questions = shuffled.slice(0, Math.min(10, shuffled.length)).map(word => {
      // Get 3 wrong answers
      const wrongAnswers = wordsPool
        .filter(w => w.sinhala !== word.sinhala)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
        .map(w => w.sinhala);

      const options = [...wrongAnswers, word.sinhala].sort(() => Math.random() - 0.5);

      return {
        question: word.english,
        correctAnswer: word.sinhala,
        options,
        pronunciation: word.pronunciation,
      };
    });

    setQuizState({
      currentQuestion: 0,
      score: 0,
      questions,
      answered: false,
      selectedAnswer: null,
    });
    setQuizMode(true);
  };

  const handleQuizAnswer = (answer) => {
    if (quizState.answered) return;

    const isCorrect = answer === quizState.questions[quizState.currentQuestion].correctAnswer;
    setQuizState(prev => ({
      ...prev,
      answered: true,
      selectedAnswer: answer,
      score: isCorrect ? prev.score + 1 : prev.score,
    }));
  };

  const nextQuestion = () => {
    if (quizState.currentQuestion < quizState.questions.length - 1) {
      setQuizState(prev => ({
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
        answered: false,
        selectedAnswer: null,
      }));
    }
  };

  const categoryKeys = Object.keys(categories);

  // Render word card
  const renderWordCard = (word, index, showCategory = false) => (
    <div key={index} className={`word-card ${word.spoken ? 'has-spoken' : ''}`}>
      <div className="word-info">
        <div className="word-english">
          {word.english}
          {showCategory && <span className="word-category-tag">{word.categoryName}</span>}
        </div>
        <div className="word-sinhala">
          {word.sinhala}
          {word.spoken && <span className="word-tag formal">formal</span>}
        </div>
        <div className="word-pronunciation">{word.pronunciation}</div>
        {word.spoken && (
          <button
            type="button"
            className="spoken-variant"
            onClick={(e) => handleSpeakSpoken(e, word.spoken.sinhala, `spoken-${index}`)}
          >
            <span className="word-tag spoken">spoken</span>
            <span className="spoken-sinhala">{word.spoken.sinhala}</span>
            <span className="spoken-pronunciation">{word.spoken.pronunciation}</span>
            <span className={`spoken-speaker ${playingSpoken === `spoken-${index}` ? 'playing' : ''}`}>
              {playingSpoken === `spoken-${index}` ? '⏹️' : '🔊'}
            </span>
          </button>
        )}
        {word.example && (
          <button className="example-toggle" onClick={() => toggleExample(index)}>
            {showExamples[index] ? '▼ Hide example' : '▶ Show example'}
          </button>
        )}
        {showExamples[index] && word.example && (
          <div className="word-example">
            <div className="example-sinhala">{word.example}</div>
            <div className="example-english">{word.exampleEn}</div>
          </div>
        )}
      </div>
      <div className="word-actions">
        <button
          className={`favorite-btn ${isFavorite(word) ? 'active' : ''}`}
          onClick={() => toggleFavorite(word)}
          title={isFavorite(word) ? "Remove from favorites" : "Add to favorites"}
        >
          {isFavorite(word) ? '⭐' : '☆'}
        </button>
        <button
          className={`speak-btn ${playingWord === index ? 'playing' : ''}`}
          onClick={() => handleSpeak(word.sinhala, index)}
          title="Listen to pronunciation"
        >
          {playingWord === index ? '⏹️' : '🔊'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="dictionary-container">
      <style>{`
        .dictionary-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .dictionary-header {
          text-align: center;
          margin-bottom: 20px;
        }

        .dictionary-header h1 {
          font-size: 2rem;
          color: var(--text-primary);
          margin-bottom: 10px;
        }

        .dictionary-header p {
          color: var(--text-secondary);
          font-size: 1.1rem;
        }

        /* Word of the Day */
        .word-of-day {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px;
          border-radius: 16px;
          margin-bottom: 20px;
          text-align: center;
        }

        .word-of-day-label {
          font-size: 0.85rem;
          opacity: 0.9;
          margin-bottom: 8px;
        }

        .word-of-day .wod-sinhala {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 4px;
        }

        .word-of-day .wod-english {
          font-size: 1.2rem;
          margin-bottom: 4px;
        }

        .word-of-day .wod-pronunciation {
          font-size: 0.95rem;
          opacity: 0.85;
          font-style: italic;
        }

        /* Search Bar */
        .search-container {
          margin-bottom: 20px;
        }

        .search-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
        }

        .search-input {
          flex: 1;
          padding: 14px 20px;
          border: 2px solid var(--border-color);
          border-radius: 12px;
          font-size: 1rem;
          background: var(--bg-card);
          color: var(--text-primary);
          transition: border-color 0.2s;
        }

        .search-input:focus {
          outline: none;
          border-color: #667eea;
        }

        .search-input::placeholder {
          color: var(--text-muted);
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .action-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .action-btn.favorites {
          background: #FFC107;
          color: #333;
        }

        .action-btn.quiz {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .action-btn.active {
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3);
        }

        /* Search Results */
        .search-results {
          background: var(--bg-card);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }

        .search-results h3 {
          margin: 0 0 16px 0;
          color: var(--text-primary);
        }

        /* Quiz Mode */
        .quiz-container {
          background: var(--bg-card);
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          text-align: center;
        }

        .quiz-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .quiz-progress {
          color: var(--text-secondary);
        }

        .quiz-score {
          background: #4CAF50;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
        }

        .quiz-question {
          font-size: 2rem;
          color: var(--text-primary);
          margin-bottom: 30px;
        }

        .quiz-options {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
          margin-bottom: 20px;
        }

        .quiz-option {
          padding: 20px;
          border: 2px solid var(--border-color);
          border-radius: 12px;
          background: var(--bg-secondary);
          cursor: pointer;
          font-size: 1.3rem;
          transition: all 0.2s;
          color: var(--text-primary);
        }

        .quiz-option:hover:not(.answered) {
          border-color: #667eea;
          background: var(--bg-card-hover);
        }

        .quiz-option.correct {
          background: #d4edda;
          border-color: #28a745;
          color: #155724;
        }

        .quiz-option.incorrect {
          background: #f8d7da;
          border-color: #dc3545;
          color: #721c24;
        }

        .quiz-option.answered {
          cursor: default;
        }

        .quiz-result {
          margin-top: 20px;
          padding: 15px;
          border-radius: 10px;
          font-size: 1.1rem;
        }

        .quiz-result.correct {
          background: #d4edda;
          color: #155724;
        }

        .quiz-result.incorrect {
          background: #f8d7da;
          color: #721c24;
        }

        .quiz-final {
          text-align: center;
          padding: 40px;
        }

        .quiz-final h2 {
          font-size: 2rem;
          margin-bottom: 20px;
          color: var(--text-primary);
        }

        .quiz-final-score {
          font-size: 4rem;
          font-weight: bold;
          color: #4CAF50;
          margin-bottom: 10px;
        }

        .quiz-btn {
          padding: 12px 30px;
          border: none;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          margin: 5px;
        }

        .quiz-btn.primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .quiz-btn.secondary {
          background: var(--bg-secondary);
          color: var(--text-primary);
          border: 2px solid var(--border-color);
        }

        .quiz-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .category-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 16px;
          margin-bottom: 30px;
        }

        .category-box {
          background: var(--bg-card);
          border-radius: 16px;
          padding: 20px 14px;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          border: 3px solid transparent;
        }

        .category-box:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .category-box.active {
          border-color: currentColor;
          transform: translateY(-3px);
        }

        .category-icon {
          font-size: 2.5rem;
          margin-bottom: 10px;
          display: block;
        }

        .category-name {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 2px;
        }

        .category-name-si {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .category-count {
          font-size: 0.75rem;
          color: var(--text-muted);
          margin-top: 6px;
        }

        .words-section {
          background: var(--bg-card);
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .words-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 15px;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid var(--border-color);
        }

        .words-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .words-title-icon {
          font-size: 2rem;
        }

        .words-title h2 {
          margin: 0;
          font-size: 1.5rem;
          color: var(--text-primary);
        }

        .words-title-si {
          color: var(--text-secondary);
          font-size: 1rem;
        }

        .words-header-actions {
          display: flex;
          gap: 10px;
        }

        .back-btn {
          background: var(--bg-secondary);
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
          transition: all 0.2s ease;
          color: var(--text-primary);
        }

        .back-btn:hover {
          background: var(--bg-card-hover);
        }

        .words-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }

        .word-card {
          background: var(--bg-secondary);
          border-radius: 12px;
          padding: 16px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
          transition: all 0.2s ease;
        }

        .word-card:hover {
          background: var(--bg-card-hover);
        }

        .word-info {
          flex: 1;
          min-width: 0;
        }

        .word-english {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-primary);
          text-transform: capitalize;
          margin-bottom: 4px;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .word-category-tag {
          font-size: 0.7rem;
          background: var(--border-color);
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: normal;
          text-transform: none;
        }

        .word-sinhala {
          font-size: 1.3rem;
          color: #1a73e8;
          margin-bottom: 4px;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .word-tag {
          display: inline-block;
          font-size: 0.65rem;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .word-tag.formal {
          background: rgba(33, 150, 243, 0.2);
          color: #1976d2;
        }

        .word-tag.spoken {
          background: rgba(76, 175, 80, 0.9);
          color: white;
        }

        .spoken-variant {
          margin-top: 8px;
          padding: 8px 10px;
          background: rgba(76, 175, 80, 0.1);
          border: none;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          transition: background 0.2s;
          width: 100%;
          text-align: left;
          font-family: inherit;
        }

        .spoken-variant:hover {
          background: rgba(76, 175, 80, 0.2);
        }

        .spoken-variant:active {
          background: rgba(76, 175, 80, 0.3);
        }

        .spoken-sinhala {
          font-size: 1.1rem;
          color: #2e7d32;
          font-weight: 500;
        }

        .spoken-pronunciation {
          font-size: 0.85rem;
          color: #558b2f;
          font-style: italic;
        }

        .spoken-speaker {
          margin-left: auto;
          font-size: 1rem;
          padding: 4px 8px;
          background: rgba(76, 175, 80, 0.2);
          border-radius: 50%;
          transition: all 0.2s;
        }

        .spoken-speaker:hover {
          background: rgba(76, 175, 80, 0.4);
          transform: scale(1.1);
        }

        .spoken-speaker.playing {
          animation: pulse 1s infinite;
        }

        .word-pronunciation {
          font-size: 0.9rem;
          color: var(--text-muted);
          font-style: italic;
        }

        .example-toggle {
          background: none;
          border: none;
          color: #667eea;
          cursor: pointer;
          font-size: 0.85rem;
          padding: 4px 0;
          margin-top: 6px;
        }

        .example-toggle:hover {
          text-decoration: underline;
        }

        .word-example {
          margin-top: 8px;
          padding: 10px;
          background: rgba(102, 126, 234, 0.1);
          border-radius: 8px;
          border-left: 3px solid #667eea;
        }

        .example-sinhala {
          font-size: 1rem;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .example-english {
          font-size: 0.85rem;
          color: var(--text-secondary);
          font-style: italic;
        }

        .word-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .favorite-btn {
          background: transparent;
          border: none;
          font-size: 1.3rem;
          cursor: pointer;
          padding: 5px;
          transition: transform 0.2s;
        }

        .favorite-btn:hover {
          transform: scale(1.2);
        }

        .favorite-btn.active {
          color: #FFC107;
        }

        .speak-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .speak-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        }

        .speak-btn.playing {
          animation: pulse 1s infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .empty-state {
          text-align: center;
          padding: 40px;
          color: var(--text-muted);
        }

        .empty-state-icon {
          font-size: 3rem;
          margin-bottom: 15px;
        }

        @media (max-width: 768px) {
          .category-grid {
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 12px;
          }

          .category-box {
            padding: 14px 10px;
          }

          .category-icon {
            font-size: 2rem;
          }

          .category-name {
            font-size: 0.85rem;
          }

          .words-grid {
            grid-template-columns: 1fr;
          }

          .words-header {
            flex-direction: column;
            text-align: center;
          }

          .quiz-options {
            grid-template-columns: 1fr;
          }

          .quiz-question {
            font-size: 1.5rem;
          }

          .action-buttons {
            justify-content: center;
          }

          .search-bar {
            flex-direction: column;
          }
        }
      `}</style>

      <div className="dictionary-header">
        <h1>📚 Sinhala Dictionary</h1>
        <p>සිංහල ශබ්දකෝෂය - Explore {allWords.length}+ words across {categoryKeys.length} categories</p>
      </div>

      {/* Word of the Day */}
      {!quizMode && !selectedCategory && !showFavorites && !searchQuery && (
        <div className="word-of-day">
          <div className="word-of-day-label">✨ Word of the Day</div>
          <div className="wod-sinhala">{wordOfTheDay.sinhala}</div>
          <div className="wod-english">{wordOfTheDay.english}</div>
          <div className="wod-pronunciation">{wordOfTheDay.pronunciation}</div>
        </div>
      )}

      {/* Search Bar */}
      {!quizMode && (
        <div className="search-container">
          <div className="search-bar">
            <input
              type="text"
              className="search-input"
              placeholder="🔍 Search in English, Sinhala, or pronunciation..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedCategory(null);
                setShowFavorites(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!quizMode && (
        <div className="action-buttons">
          <button
            className={`action-btn favorites ${showFavorites ? 'active' : ''}`}
            onClick={() => {
              setShowFavorites(!showFavorites);
              setSelectedCategory(null);
              setSearchQuery('');
            }}
          >
            ⭐ Favorites ({favorites.length})
          </button>
          <button
            className="action-btn quiz"
            onClick={() => startQuiz()}
          >
            🎯 Start Quiz
          </button>
        </div>
      )}

      {/* Quiz Mode */}
      {quizMode && (
        <div className="quiz-container">
          {quizState.currentQuestion < quizState.questions.length ? (
            <>
              <div className="quiz-header">
                <span className="quiz-progress">
                  Question {quizState.currentQuestion + 1} of {quizState.questions.length}
                </span>
                <span className="quiz-score">Score: {quizState.score}</span>
              </div>

              <div className="quiz-question">
                What is "{quizState.questions[quizState.currentQuestion].question}" in Sinhala?
              </div>

              <div className="quiz-options">
                {quizState.questions[quizState.currentQuestion].options.map((option, idx) => {
                  let className = 'quiz-option';
                  if (quizState.answered) {
                    className += ' answered';
                    if (option === quizState.questions[quizState.currentQuestion].correctAnswer) {
                      className += ' correct';
                    } else if (option === quizState.selectedAnswer) {
                      className += ' incorrect';
                    }
                  }
                  return (
                    <button
                      key={idx}
                      className={className}
                      onClick={() => handleQuizAnswer(option)}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>

              {quizState.answered && (
                <>
                  <div className={`quiz-result ${quizState.selectedAnswer === quizState.questions[quizState.currentQuestion].correctAnswer ? 'correct' : 'incorrect'}`}>
                    {quizState.selectedAnswer === quizState.questions[quizState.currentQuestion].correctAnswer
                      ? '✅ Correct!'
                      : `❌ Incorrect. The answer is: ${quizState.questions[quizState.currentQuestion].correctAnswer}`}
                    <br />
                    <small>Pronunciation: {quizState.questions[quizState.currentQuestion].pronunciation}</small>
                  </div>
                  <button className="quiz-btn primary" onClick={nextQuestion}>
                    {quizState.currentQuestion < quizState.questions.length - 1 ? 'Next Question →' : 'See Results'}
                  </button>
                </>
              )}

              <button className="quiz-btn secondary" onClick={() => setQuizMode(false)} style={{ marginTop: '20px' }}>
                Exit Quiz
              </button>
            </>
          ) : (
            <div className="quiz-final">
              <h2>🎉 Quiz Complete!</h2>
              <div className="quiz-final-score">{quizState.score}/{quizState.questions.length}</div>
              <p>
                {quizState.score === quizState.questions.length
                  ? 'Perfect score! Amazing!'
                  : quizState.score >= quizState.questions.length * 0.7
                  ? 'Great job! Keep learning!'
                  : 'Keep practicing! You\'ll get better!'}
              </p>
              <button className="quiz-btn primary" onClick={() => startQuiz()}>
                Try Again
              </button>
              <button className="quiz-btn secondary" onClick={() => setQuizMode(false)}>
                Back to Dictionary
              </button>
            </div>
          )}
        </div>
      )}

      {/* Search Results */}
      {!quizMode && searchQuery && searchResults.length > 0 && (
        <div className="search-results">
          <h3>Search Results ({searchResults.length})</h3>
          <div className="words-grid">
            {searchResults.map((word, index) => renderWordCard(word, `search-${index}`, true))}
          </div>
        </div>
      )}

      {!quizMode && searchQuery && searchResults.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <p>No results found for "{searchQuery}"</p>
        </div>
      )}

      {/* Favorites View */}
      {!quizMode && showFavorites && !searchQuery && (
        <div className="words-section">
          <div className="words-header">
            <div className="words-title">
              <span className="words-title-icon">⭐</span>
              <div>
                <h2>My Favorites</h2>
                <span className="words-title-si">{favorites.length} saved words</span>
              </div>
            </div>
            <button className="back-btn" onClick={() => setShowFavorites(false)}>
              ← Back
            </button>
          </div>

          {favoriteWords.length > 0 ? (
            <div className="words-grid">
              {favoriteWords.map((word, index) => renderWordCard(word, `fav-${index}`, true))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">⭐</div>
              <p>No favorites yet. Click the star on any word to save it!</p>
            </div>
          )}
        </div>
      )}

      {/* Category Grid or Words List */}
      {!quizMode && !showFavorites && !searchQuery && (
        <>
          {!selectedCategory ? (
            <div className="category-grid">
              {categoryKeys.map((key) => {
                const cat = categories[key];
                return (
                  <div
                    key={key}
                    className="category-box"
                    style={{ color: cat.color }}
                    onClick={() => setSelectedCategory(key)}
                  >
                    <span className="category-icon">{cat.icon}</span>
                    <div className="category-name">{cat.name}</div>
                    <div className="category-name-si">{cat.nameSi}</div>
                    <div className="category-count">{cat.words.length} words</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="words-section">
              <div className="words-header">
                <div className="words-title">
                  <span className="words-title-icon">{categories[selectedCategory].icon}</span>
                  <div>
                    <h2>{categories[selectedCategory].name}</h2>
                    <span className="words-title-si">{categories[selectedCategory].nameSi}</span>
                  </div>
                </div>
                <div className="words-header-actions">
                  <button className="action-btn quiz" onClick={() => startQuiz(selectedCategory)}>
                    🎯 Quiz this category
                  </button>
                  <button className="back-btn" onClick={() => setSelectedCategory(null)}>
                    ← Back
                  </button>
                </div>
              </div>

              <div className="words-grid">
                {categories[selectedCategory].words.map((word, index) => renderWordCard(word, index))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dictionary;
